import React, { useState } from 'react';
import { X, Star, Camera, Send, CheckCircle, Loader2, Trash2 } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

// Star Rating Component
const StarRating = ({ rating, setRating, label, description }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {rating > 0 ? `${rating}/5` : 'Not rated'}
        </span>
      </div>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{description}</p>
      )}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="btn-press p-1 transition-transform hover:scale-110"
          >
            <Star
              size={28}
              className={`transition-colors ${
                star <= (hover || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const FeedbackModal = ({ isOpen, onClose, user, groupName }) => {
  const [ratings, setRatings] = useState({
    overall: 0,
    easeOfUse: 0,
    features: 0,
    design: 0,
    performance: 0,
  });
  const [favoriteFeature, setFavoriteFeature] = useState('');
  const [missingFeature, setMissingFeature] = useState('');
  const [bugs, setBugs] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 1MB for base64 in Firestore)
      if (file.size > 1 * 1024 * 1024) {
        setError('Image must be less than 1MB');
        return;
      }
      setScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    

    setSubmitting(true);
    setError('');

    try {
      let screenshotBase64 = null;

      // Convert screenshot to base64 if provided
      if (screenshot) {
        screenshotBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(screenshot);
        });
      }

      // Prepare feedback data
      const feedbackData = {
        // User info
        userId: user.uid,
        userEmail: user.email,
        groupName: groupName || 'No group',
        
        // Ratings
        ratings: {
          overall: ratings.overall,
          easeOfUse: ratings.easeOfUse,
          features: ratings.features,
          design: ratings.design,
          performance: ratings.performance,
        },
        
        // Text feedback
        favoriteFeature: favoriteFeature.trim(),
        missingFeature: missingFeature.trim(),
        bugs: bugs.trim(),
        additionalNotes: additionalNotes.trim(),
        
        // Screenshot as base64 (or null)
        screenshotUrl: screenshotBase64,
        
        // Metadata
        timestamp: serverTimestamp(),
        appVersion: '1.0.0-beta', // Update this as needed
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        platform: navigator.platform,
        
        // Status for admin tracking
        status: 'new', // new, reviewed, addressed
        adminNotes: '',
      };

      // Save to Firebase
      await addDoc(collection(db, 'feedback'), feedbackData);

      setSubmitted(true);
      
      // Reset form after delay
      setTimeout(() => {
        onClose();
        // Reset state for next time
        setTimeout(() => {
          setSubmitted(false);
          setRatings({ overall: 0, easeOfUse: 0, features: 0, design: 0, performance: 0 });
          setFavoriteFeature('');
          setMissingFeature('');
          setBugs('');
          setAdditionalNotes('');
          removeScreenshot();
        }, 300);
      }, 2000);

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="modal-enter bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
              Share Your Feedback
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Help us improve Boply!
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-press p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="success-pop w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Thank You! 🎉
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Your feedback helps us make Boply better for everyone.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Star Ratings */}
              <div className="space-y-4">
                <StarRating
                  rating={ratings.overall}
                  setRating={(val) => setRatings({ ...ratings, overall: val })}
                  label="Overall Experience"
                  description="How would you rate Boply overall?"
                />
                
                <StarRating
                  rating={ratings.easeOfUse}
                  setRating={(val) => setRatings({ ...ratings, easeOfUse: val })}
                  label="Ease of Use"
                  description="How easy is the app to navigate?"
                />
                
                <StarRating
                  rating={ratings.features}
                  setRating={(val) => setRatings({ ...ratings, features: val })}
                  label="Features"
                  description="Do the features meet your needs?"
                />
                
                <StarRating
                  rating={ratings.design}
                  setRating={(val) => setRatings({ ...ratings, design: val })}
                  label="Design & Look"
                  description="How do you like the visual design?"
                />
                
                <StarRating
                  rating={ratings.performance}
                  setRating={(val) => setRatings({ ...ratings, performance: val })}
                  label="Speed & Performance"
                  description="Does the app feel fast and responsive?"
                />
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* Text Questions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    What's your favorite feature?
                  </label>
                  <input
                    type="text"
                    value={favoriteFeature}
                    onChange={(e) => setFavoriteFeature(e.target.value)}
                    placeholder="e.g., The calendar view is great!"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    What feature is missing?
                  </label>
                  <input
                    type="text"
                    value={missingFeature}
                    onChange={(e) => setMissingFeature(e.target.value)}
                    placeholder="e.g., I wish it had setlist management"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Any bugs or issues?
                  </label>
                  <textarea
                    value={bugs}
                    onChange={(e) => setBugs(e.target.value)}
                    placeholder="Describe any problems you've encountered..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Anything else you'd like to share..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Screenshot Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add a Screenshot (optional)
                </label>
                
                {screenshotPreview ? (
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt="Screenshot preview"
                      className="w-full h-40 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="btn-press absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="card-tap flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-purple-400 dark:hover:border-purple-500 transition-colors bg-gray-50 dark:bg-gray-700/50">
                    <Camera size={32} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Click to upload screenshot
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      PNG, JPG up to 1MB
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-press flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-press flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;